import * as React from "react";
import { useEffect, useRef, useState } from "react";
import {
	Typography,
	Grid,
	Checkbox,
	Stack,
	Snackbar,
	CircularProgress,
	Alert
} from "@mui/material";
import { StyledTextBox } from "../../../components/TextBox";
import { ContainedSearchButton } from "../../../components/Buttons";
import { useParams } from "react-router-dom";
import {
	GetBankInformation,
	UpdateBankInformation,
} from "../../../services/client.services";
import { LoadingBox } from "../../../components/Box";

export default function BankInformation() {
	const { id } = useParams();
	const [isLoading, setIsLoading] = useState(false);
	const [formFields, setFormFields] = useState(null);
	const [fetchError, setFetchError] = useState(false);
	const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
	const hasRun = useRef(false);

	useEffect(() => {
		if (hasRun.current) return;
		hasRun.current = true;

		const fetchData = async () => {
			setIsLoading(true);
			try {
				const data = await GetBankInformation(id);
				if (data) {
					setFormFields({
						accountName: { value: data.accountName || "", error: false, message: "" },
						accountNumber: { value: data.accountNumber || "", error: false, message: "" },
						accountHolderName: { value: data.accountHolderName || "", error: false, message: "" },
						swiftBicNo: { value: data.swiftBicNo || "", error: false, message: "" },
						routingNo: { value: data.routingNo || "", error: false, message: "" },
						payAccountType: { value: data.payAccountType || "", error: false, message: "" },
						bankName: { value: data.bankName || "", error: false, message: "" },
						bankBranchCode: { value: data.bankBranchCode || "", error: false, message: "" },
						bankAddress: { value: data.bankAddress || "", error: false, message: "" },
						bankCity: { value: data.bankCity || "", error: false, message: "" },
						bankState: { value: data.bankState || "", error: false, message: "" },
						zipCode: { value: data.zipCode || "", error: false, message: "" },
						country: { value: data.country || "", error: false, message: "" },
						currency: { value: data.currency || "", error: false, message: "" },
						id: { value: data.id || "", error: false, message: "" },
					});
				} else {
					setFetchError(true)
				}

			} catch (error) {
				console.error("Error fetching client data", error);
				setSnackbar({ open: true, message: "Failed to fetch client info.", severity: 'error' });
			} finally {
				setIsLoading(false);
			}
		};

		fetchData();
	}, [id]);

	const updateField = (field, value) => {
		setFormFields((prev) => ({
			...prev,
			[field]: {
				...prev[field],
				value,
				error: false,
				message: "",
			},
		}));
	};

	const validateFields = () => {
		let hasError = false;
		const requiredFields = ["accountName", "accountNumber", "accountHolderName", "swiftBicNo", "routingNo", "payAccountType", "bankName", "bankBranchCode", "bankAddress", "bankCity", "bankState", "zipCode", "country", "currency"];
		const updatedFields = { ...formFields };

		requiredFields.forEach((field) => {
			if (!formFields[field].value.trim()) {
				updatedFields[field].error = true;
				updatedFields[field].message = "This field is required";
				hasError = true;
			}
		});
		setFormFields(updatedFields);
		return !hasError;
	};

	const handleSave = async () => {
		if (!validateFields()) return;

		const payload = {
			id: formFields.id.value,
			accountName: formFields.accountName.value,
			accountNumber: formFields.accountNumber.value,
			accountHolderName: formFields.accountHolderName.value,
			swiftBicNo: formFields.swiftBicNo.value,
			routingNo: formFields.routingNo.value,
			payAccountType: formFields.payAccountType.value,
			bankName: formFields.bankName.value,
			bankBranchCode: formFields.bankBranchCode.value,
			bankAddress: formFields.bankAddress.value,
			bankCity: formFields.bankCity.value,
			bankState: formFields.bankState.value,
			zipCode: formFields.zipCode.value,
			country: formFields.country.value,
			currency: formFields.currency.value
		};

		try {
			setIsLoading(true);
			await UpdateBankInformation(payload);
			setSnackbar({ open: true, message: "Client information updated successfully!", severity: 'success' });
		} catch (error) {
			setSnackbar({ open: true, message: "Update failed. Try again later.", severity: 'error' });
		} finally {
			setIsLoading(false);
		}
	};

	if (!formFields || isLoading) {
		return (
			<LoadingBox>
				{fetchError ?
					(
						<Typography variant="body2" color="neutral.letter_Black">
							Seller Bank details Not Found
						</Typography>
					) :
					(
						<CircularProgress />
					)
				}
			</LoadingBox>
		);
	}

	return (
		<div>
			<Typography variant="body1">
				Account Information.
			</Typography>
			<br></br>
			<Grid container justifyContent="flex-start">
				<Grid xs={5} container direction="row" alignItems="center">
					<Grid xs={3}>
						<Typography variant="body2" color="neutral.letter_Black">
							Account Number
						</Typography>
					</Grid>
					<Grid xs={9}>
						<StyledTextBox
							fullWidth
							value={formFields.accountNumber.value}
							error={formFields.accountNumber.error}
							helperText={formFields.accountNumber.message}
							onChange={(e) => updateField("accountNumber", e.target.value)}
						/>
					</Grid>
				</Grid>
				<Grid xs={1} />
				<Grid xs={5} container direction="row" alignItems="center">
					<Grid xs={3}>
						<Typography variant="body2" color="neutral.letter_Black">
							Account Holder Name
						</Typography>
					</Grid>
					<Grid xs={9}>
						<StyledTextBox
							fullWidth
							value={formFields.accountHolderName.value}
							error={formFields.accountHolderName.error}
							helperText={formFields.accountHolderName.message}
							onChange={(e) => updateField("accountHolderName", e.target.value)}
						/>
					</Grid>
				</Grid>
			</Grid>
			<br></br>
			<Grid container justifyContent="flex-start">
				<Grid xs={5} container direction="row" alignItems="center">
					<Grid xs={3}>
						<Typography variant="body2" color="neutral.letter_Black">
							SWIFT/IBAN Number
						</Typography>
					</Grid>
					<Grid xs={9}>
						<StyledTextBox
							fullWidth
							value={formFields.swiftBicNo.value}
							error={formFields.swiftBicNo.error}
							helperText={formFields.swiftBicNo.message}
							onChange={(e) => updateField("swiftBicNo", e.target.value)}
						/>
					</Grid>
				</Grid>
				<Grid xs={1} />
				<Grid xs={5} container direction="row" alignItems="center">
					<Grid xs={3}>
						<Typography variant="body2" color="neutral.letter_Black">
							Routing Number
						</Typography>
					</Grid>
					<Grid xs={9}>
						<StyledTextBox
							fullWidth
							value={formFields.routingNo.value}
							error={formFields.routingNo.error}
							helperText={formFields.routingNo.message}
							onChange={(e) => updateField("routingNo", e.target.value)}
						/>
					</Grid>
				</Grid>
			</Grid>

			<br></br>
			<Grid container justifyContent="flex-start">
				<Grid xs={5} container direction="row" alignItems="center">
					<Grid xs={3}>
						<Typography variant="body2" color="neutral.letter_Black">
							Type
						</Typography>
					</Grid>
					<Grid xs={9}>
						<StyledTextBox
							fullWidth
							value={formFields.payAccountType.value}
							error={formFields.payAccountType.error}
							helperText={formFields.payAccountType.message}
							onChange={(e) => updateField("payAccountType", e.target.value)}
						/>
					</Grid>
				</Grid>
				<Grid xs={1} />
			</Grid>
			<br></br>
			<Typography variant="body1">
				Bank Information.
			</Typography>
			<br></br>
			<Grid container justifyContent="flex-start">
				<Grid xs={5} container direction="row" alignItems="center">
					<Grid xs={3}>
						<Typography variant="body2" color="neutral.letter_Black">
							Bank Name
						</Typography>
					</Grid>
					<Grid xs={9}>
						<StyledTextBox
							fullWidth
							value={formFields.bankName.value}
							error={formFields.bankName.error}
							helperText={formFields.bankName.message}
							onChange={(e) => updateField("bankName", e.target.value)}
						/>
					</Grid>
				</Grid>
				<Grid xs={1} />
				<Grid xs={5} container direction="row" alignItems="center">
					<Grid xs={3}>
						<Typography variant="body2" color="neutral.letter_Black">
							Bank Branch Code
						</Typography>
					</Grid>
					<Grid xs={9}>
						<StyledTextBox
							fullWidth
							value={formFields.bankBranchCode.value}
							error={formFields.bankBranchCode.error}
							helperText={formFields.bankBranchCode.message}
							onChange={(e) => updateField("bankBranchCode", e.target.value)}
						/>
					</Grid>
				</Grid>
			</Grid>
			<br></br>
			<Grid container justifyContent="flex-start">
				<Grid xs={5} container direction="row" alignItems="center">
					<Grid xs={3}>
						<Typography variant="body2" color="neutral.letter_Black">
							Bank Address
						</Typography>
					</Grid>
					<Grid xs={9}>
						<StyledTextBox
							fullWidth
							value={formFields.bankAddress.value}
							error={formFields.bankAddress.error}
							helperText={formFields.bankAddress.message}
							onChange={(e) => updateField("bankAddress", e.target.value)}
						/>
					</Grid>
				</Grid>
				<Grid xs={1} />
				<Grid xs={5} container direction="row" alignItems="center">
					<Grid xs={3}>
						<Typography variant="body2" color="neutral.letter_Black">
							Bank City
						</Typography>
					</Grid>
					<Grid xs={9}>
						<StyledTextBox
							fullWidth
							value={formFields.bankCity.value}
							error={formFields.bankCity.error}
							helperText={formFields.bankCity.message}
							onChange={(e) => updateField("bankCity", e.target.value)}
						/>
					</Grid>
				</Grid>
			</Grid>
			<br></br>
			<Grid container justifyContent="flex-start">
				<Grid xs={5} container direction="row" alignItems="center">
					<Grid xs={3}>
						<Typography variant="body2" color="neutral.letter_Black">
							Bank State
						</Typography>
					</Grid>
					<Grid xs={9}>
						<StyledTextBox
							fullWidth
							value={formFields.bankState.value}
							error={formFields.bankState.error}
							helperText={formFields.bankState.message}
							onChange={(e) => updateField("bankState", e.target.value)}
						/>
					</Grid>
				</Grid>
				<Grid xs={1} />
				<Grid xs={5} container direction="row" alignItems="center">
					<Grid xs={3}>
						<Typography variant="body2" color="neutral.letter_Black">
							Zip Code
						</Typography>
					</Grid>
					<Grid xs={9}>
						<StyledTextBox
							fullWidth
							value={formFields.zipCode.value}
							error={formFields.zipCode.error}
							helperText={formFields.zipCode.message}
							onChange={(e) => updateField("zipCode", e.target.value)}
						/>
					</Grid>
				</Grid>
			</Grid>
			<br></br>
			<Grid container justifyContent="flex-start">
				<Grid xs={5} container direction="row" alignItems="center">
					<Grid xs={3}>
						<Typography variant="body2" color="neutral.letter_Black">
							Country
						</Typography>
					</Grid>
					<Grid xs={9}>
						<StyledTextBox
							fullWidth
							value={formFields.country.value}
							error={formFields.country.error}
							helperText={formFields.country.message}
							onChange={(e) => updateField("country", e.target.value)}
						/>
					</Grid>
				</Grid>
				<Grid xs={1} />
				<Grid xs={5} container direction="row" alignItems="center">
					<Grid xs={3}>
						<Typography variant="body2" color="neutral.letter_Black">
							Currency
						</Typography>
					</Grid>
					<Grid xs={9}>
						<StyledTextBox
							fullWidth
							value={formFields.currency.value}
							error={formFields.currency.error}
							helperText={formFields.currency.message}
							onChange={(e) => updateField("currency", e.target.value)}
						/>
					</Grid>
				</Grid>
			</Grid>
			<br></br>
			<Stack direction="row" spacing={1} alignItems="center">
				<Checkbox />
				<Typography variant="body2" color="neutral.letter_Black">
					Notify the changes for user.
				</Typography>
			</Stack>
			<br></br>
			<ContainedSearchButton variant="contained" onClick={handleSave}>
				Save changes
			</ContainedSearchButton>
			<Snackbar
				open={snackbar.open}
				autoHideDuration={4000}
				onClose={() => setSnackbar({ ...snackbar, open: false })}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert severity={snackbar.severity} sx={{ width: "100%" }}>
					{snackbar.message}
				</Alert>
			</Snackbar>
		</div>
	);
}
